import React from 'react';
import Box from '@mui/material/Box';
import GeneratorPanel from './GeneratorPanel';
import { GeneratorMark } from './PedigreeGlyph';
import { ACCENT_FALLBACK, CHROME } from './tokens';

const POP_IN = 'pedigree-ai-pop-in';
const POP_OUT = 'pedigree-ai-pop-out';
const PILL_IN = 'pedigree-ai-pill-in';

const OPEN_MS = 220;
const CLOSE_MS = 160;

const EASE_IN = 'cubic-bezier(0.2, 0, 0, 1)';
const EASE_OUT = 'cubic-bezier(0.4, 0, 1, 1)';

// Guarded: jsdom has no matchMedia.
function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default function PedigreeAIGenerator({
  accent = ACCENT_FALLBACK,
  anchor = { right: 28 },
  onApply,
  disabled = false
}) {
  // Three phases, not a boolean: the panel stays mounted through 'closing' so
  // it collapses back into the button instead of blinking out.
  const [phase, setPhase] = React.useState('closed');
  const open = phase !== 'closed';

  const requestClose = React.useCallback(() => {
    setPhase((current) => (current === 'open' ? 'closing' : current));
  }, []);

  // A timer rather than animationend: under reduced motion the animation is
  // `none` and animationend never fires, leaving the panel stuck open.
  React.useEffect(() => {
    if (phase !== 'closing') return undefined;
    const timer = window.setTimeout(
      () => setPhase('closed'),
      prefersReducedMotion() ? 0 : CLOSE_MS
    );
    return () => window.clearTimeout(timer);
  }, [phase]);

  React.useEffect(() => {
    if (phase !== 'open') return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, requestClose]);

  if (disabled) return null;

  // Not caught on purpose: the panel closes only once the write succeeded.
  const handleApply = async (result, options) => {
    await onApply?.(result, options);
    requestClose();
  };

  return (
    <Box
      data-va-anchor="pedigree.aiGenerator"
      sx={{
        position: 'fixed',
        ...anchor,
        bottom: 'calc(var(--tabbar-offset, 0px) + 16px)',
        zIndex: 1200, // above the tab strip (appBar), below MUI dialogs/menus
        display: 'flex',
        justifyContent: anchor.left != null ? 'flex-start' : 'flex-end'
      }}
    >
      {open ? (
        // Scaled from the launcher's corner so the two read as one object.
        <Box
          sx={{
            transformOrigin:
              anchor.left != null ? 'bottom left' : 'bottom right',
            animation:
              phase === 'closing'
                ? `${POP_OUT} ${CLOSE_MS}ms ${EASE_OUT} forwards`
                : `${POP_IN} ${OPEN_MS}ms ${EASE_IN}`,
            [`@keyframes ${POP_IN}`]: {
              '0%': { opacity: 0, transform: 'scale(0)' },
              '100%': { opacity: 1, transform: 'scale(1)' }
            },
            [`@keyframes ${POP_OUT}`]: {
              '0%': { opacity: 1, transform: 'scale(1)' },
              '100%': { opacity: 0, transform: 'scale(0)' }
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
          }}
        >
          <GeneratorPanel
            accent={accent}
            onClose={requestClose}
            onApply={handleApply}
          />
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => setPhase('open')}
          aria-label="Sketch a pedigree with AI"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 40,
            pl: 1.5,
            pr: 2,
            cursor: 'pointer',
            borderRadius: 999,
            backgroundColor: CHROME.surface,
            border: `1px solid ${CHROME.border}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            color: CHROME.text,
            fontSize: '0.8rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            animation: `${PILL_IN} 120ms ${EASE_IN}`,
            [`@keyframes ${PILL_IN}`]: {
              '0%': { opacity: 0, transform: 'scale(0.85)' },
              '100%': { opacity: 1, transform: 'scale(1)' }
            },
            transition: 'box-shadow 140ms ease, transform 140ms ease',
            '&:hover': {
              boxShadow: '0 4px 14px rgba(15,30,38,0.18)',
              transform: 'translateY(-1px)'
            },
            '&:focus-visible': {
              outline: `2px solid ${accent}`,
              outlineOffset: 2
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              transition: 'none',
              '&:hover': { transform: 'none' }
            }
          }}
        >
          <GeneratorMark size={20} color={accent} />
          Sketch with AI
        </Box>
      )}
    </Box>
  );
}
